import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ProfitLossPageView } from './ProfitLossPageView';
import { useProfitLossPage } from './useProfitLossPage';

/**
 * `/accounting/statements/profit-loss` — the Profit & Loss screen. Owns its data
 * hook and an `ErrorBoundary`; the throw lives in the inner Content child (the
 * repo's self-served page pattern).
 */
export const ProfitLossPage: React.FC = () => {
    const page = useProfitLossPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('statements.error.title'),
            errorMessage: t('statements.error.message'),
            errorRetry: t('statements.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('profit-loss-page')}
        >
            <ErrorBoundary
                data-testid="profit-loss-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="profit-loss-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <ProfitLossPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type ProfitLossPageContentProps = {
    page: ReturnType<typeof useProfitLossPage>;
};

const ProfitLossPageContent: React.FC<ProfitLossPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its range control, presets and Print CTA while loading and
    // skeletons only the statement body — the repo's loading-state standard.
    return (
        <ProfitLossPageView
            isLoading={page.isLoading || page.rows === null}
            generatedText={page.generatedText}
            onDismissWarnings={page.onDismissWarnings}
            onViewLedger={page.onViewLedger}
            columns={page.columns}
            rows={page.rows ?? []}
            unmappedCodes={page.unmappedCodes}
            warningNotices={page.warningNotices}
        />
    );
};
